import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { formatInTimeZone } from 'date-fns-tz';
import { startOfDay, isSameDay, parseISO, format, addDays } from 'date-fns';
import { 
  GET_JOBS, 
  GET_CANCELED_JOBS,
  GET_USERS_MINIMAL 
} from '../graphql/queries';
import { ASSIGN_JOBS, CANCEL_JOB } from '../graphql/mutations';
import { formatStartOfDay, formatEndOfDay, getFormattedDateRange } from '../utils/timezone-utils';

// Remove cache keys to ensure fresh data fetching for each query
const JOBS_CACHE_KEY = 'PENDING_JOBS';
const COMPLETED_JOBS_CACHE_KEY = 'COMPLETED_JOBS';
const INSPECTION_JOBS_CACHE_KEY = 'INSPECTION_JOBS';
const DELETED_JOBS_CACHE_KEY = 'DELETED_JOBS';

// Maximum jobs per page allowed by the backend
const MAX_JOBS_PER_PAGE = 20;



// Helper function to consistently format dates to YYYY-MM-DD
const formatDateKey = (date) => {
  try {
    // Handle different input types
    let dateObj;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      // Check if it's a timestamp in string format
      if (/^\d+$/.test(date)) {
        dateObj = new Date(parseInt(date, 10));
      }
      // Special case for SQL date format (YYYY-MM-DD)
      else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date; // Already in correct format
      }
      else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      dateObj = new Date(date); // Timestamp
    } else {
      return null;
    }
  
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    
    // Format as YYYY-MM-DD without timezone issues by using local date components
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formatted = `${year}-${month}-${day}`;
    
    return formatted;
  } catch (error) {
    return null;
  }
};

export function useScheduleJobs(
  selectedDate, 
  endDate, 
  propertySearchTerm, 
  selectedCategories, 
  selectedServiceTypes,
  selectedAreas,
  user,
  teamMemberSearchTerm = '',
  serviceTypeSearchTerm = '',
  showCompleted = false,
  selectedPropertyId = null
) {
  // Debug logging removed - fix is working
  // Get Apollo client to clear cache when needed
  const client = useApolloClient();
  
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalJobs, setTotalJobs] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [forcedRefresh, setForcedRefresh] = useState(false);
  const [jobsResponse, setJobsResponse] = useState(null);
  
  // State to track total jobs by date
  const [jobsByDateTotals, setJobsByDateTotals] = useState({});
  
  // Refs to track throttling
  const lastLoadTime = useRef(0);
  
  // Create a consistent search parameter - prioritize property ID over text search
  const searchParam = useMemo(() => {
    // If a property ID is selected, don't use text search
    if (selectedPropertyId) {
      return undefined;
    }
    // Otherwise, use text search if it's long enough
    const search = propertySearchTerm && propertySearchTerm.length >= 2 ? propertySearchTerm : undefined;
    return search;
  }, [propertySearchTerm, selectedPropertyId]);
  
  // Optimize query limits for better performance
  const queryLimit = useMemo(() => {
    // Use consistent 20 jobs per page for all scenarios - let pagination handle loading more
    return 20;
  }, []);
  
  // Format dates for GraphQL queries using OPTIMIZED date ranges
  const formattedStartDate = useMemo(() => {
    // If user has selected a specific startDate via date picker, use it
    // Use isSameDay to compare dates, not times
    if (endDate && selectedDate && !isSameDay(endDate, selectedDate)) {
      return formatStartOfDay(selectedDate);
    }
    
    // For property searches, user searches, or selected property ID, use 2 weeks back to show recent jobs
    if ((propertySearchTerm && propertySearchTerm.length >= 2) || 
        selectedPropertyId || 
        (teamMemberSearchTerm && teamMemberSearchTerm.trim())) {
      // For property/user searches, look back 2 weeks to show recent jobs
      const twoWeeksBack = new Date();
      twoWeeksBack.setDate(twoWeeksBack.getDate() - 14);
      return formatStartOfDay(twoWeeksBack);
    }
    
    // For infinite scroll, start from today to show future jobs
    return formatStartOfDay(selectedDate);
  }, [selectedDate, endDate, propertySearchTerm, selectedPropertyId, teamMemberSearchTerm]);
  
  const formattedEndDate = useMemo(() => {
    // If user has selected a specific endDate via date picker, use it
    if (endDate && selectedDate && !isSameDay(endDate, selectedDate)) {
      return formatEndOfDay(endDate);
    }
    
    // For property/user searches, use 2 years forward to show all future jobs for that property
    if ((propertySearchTerm && propertySearchTerm.length >= 2) || 
        selectedPropertyId || 
        (teamMemberSearchTerm && teamMemberSearchTerm.trim())) {
      // Use 2 years forward for property searches to show all future jobs
      const twoYearsAhead = new Date();
      twoYearsAhead.setFullYear(twoYearsAhead.getFullYear() + 2);
      return formatEndOfDay(twoYearsAhead);
    }
    
    // For general browsing, use a broad date range to show many jobs for infinite scroll
    const broadEndDate = new Date();
    broadEndDate.setFullYear(broadEndDate.getFullYear() + 1); // Keep 1 year for non-search browsing
    return formatEndOfDay(broadEndDate);
  }, [endDate, selectedDate, propertySearchTerm, selectedPropertyId, teamMemberSearchTerm]);



  // Clean up variables to avoid sending empty arrays or null values
  const queryVariables = useMemo(() => {
    // Validate date range - ensure start date is not after end date
    const startDate = new Date(formattedStartDate);
    const endDate = new Date(formattedEndDate);
    
    if (startDate > endDate) {
      // Swap the dates to make them valid
      const temp = formattedStartDate;
      const correctedStartDate = formattedEndDate;
      const correctedEndDate = temp;
      
      var variables = {
        input: {
          startDate: correctedStartDate,
          endDate: correctedEndDate,
          statuses: showCompleted 
            ? ['PENDING', 'IN_PROGRESS', 'READY_FOR_INSPECTION', 'COMPLETED']
            : ['PENDING', 'IN_PROGRESS'],
          limit: queryLimit,
          offset: 0
        }
      };
    } else {
      var variables = {
        input: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          statuses: showCompleted 
            ? ['PENDING', 'IN_PROGRESS', 'READY_FOR_INSPECTION', 'COMPLETED']
            : ['PENDING', 'IN_PROGRESS'],
          limit: queryLimit,
          offset: 0
        }
      };
    }
    
    // Only add optional filters if they have values
    if (selectedCategories?.length > 0) {
      variables.input.categories = selectedCategories;
    }
    if (selectedAreas?.length > 0) {
      variables.input.areas = selectedAreas;
    }
    if (selectedServiceTypes?.length > 0) {
      variables.input.serviceTypeIds = selectedServiceTypes;
    }
    if (searchParam?.trim()) {
      variables.input.search = searchParam.trim();
    }
    if (teamMemberSearchTerm?.trim()) {
      variables.input.assignedToUserId = teamMemberSearchTerm.trim();
    }
    
    // Add property ID filter if a specific property is selected
    if (selectedPropertyId) {
      variables.input.propertyId = selectedPropertyId;
    }
    
    return variables;
  }, [formattedStartDate, formattedEndDate, showCompleted, queryLimit, selectedCategories, selectedAreas, selectedServiceTypes, searchParam, teamMemberSearchTerm, selectedPropertyId]);

  // Main jobs query with optimized caching
  const { 
    loading: jobsLoading, 
    data: jobsData, 
    refetch,
    fetchMore,
    error: jobsError 
  } = useQuery(GET_JOBS, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network', // Always check network but use cache first
    errorPolicy: 'all', // Don't break the UI on errors
    notifyOnNetworkStatusChange: true, // Important for loading states
    returnPartialData: false // Don't return partial data that could cause inconsistencies
  });



  // Users query for job assignment
  const { 
    data: usersData, 
    loading: usersLoading 
  } = useQuery(GET_USERS_MINIMAL, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all'
  });

  // Mutations
  const [assignJobsMutation] = useMutation(ASSIGN_JOBS);
  const [cancelJobMutation] = useMutation(CANCEL_JOB);

  // Process jobs data and group by date
  const pendingJobs = useMemo(() => {
    if (!jobsData?.jobs?.items) return [];
    
    // Use current jobs from the query (Apollo handles pagination)
    const currentJobs = jobsData.jobs.items || [];
    
    // Filter out cancelled jobs and group by date
    return currentJobs.filter(job => job.status !== 'CANCELLED');
  }, [jobsData?.jobs?.items]);

  const pendingJobsByDate = useMemo(() => {
    const grouped = {};
    
    pendingJobs.forEach(job => {
      const dateKey = formatDateKey(job.scheduledDate);
      if (dateKey) {
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(job);
      }
    });
    
    // Sort dates and jobs within each date
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        // Sort by start time if available, otherwise by ID
        if (a.startTime && b.startTime) {
          return new Date(a.startTime) - new Date(b.startTime);
        }
        return a.id - b.id;
      });
    });
    
    return grouped;
  }, [pendingJobs]);

  const allJobs = useMemo(() => {
    return pendingJobs;
  }, [pendingJobs]);

    // Update jobs by date totals when data changes
  useEffect(() => {
    if (jobsData?.jobs?.dateTotals) {
      const totals = {};
      jobsData.jobs.dateTotals.forEach(({ date, count }) => {
        let formattedDate;
        
        if (typeof date === 'number') {
          // Backend sends UTC midnight timestamps - extract UTC date components
          const utcDate = new Date(date);
          const year = utcDate.getUTCFullYear();
          const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getUTCDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else {
          formattedDate = formatDateKey(date);
        }
        
        if (formattedDate) {
          totals[formattedDate] = count;
        }
      });
      setJobsByDateTotals(totals);
      

    }
  }, [jobsData?.jobs?.dateTotals]);

  // Update total jobs count and use backend's hasMore
  useEffect(() => {
    if (jobsData?.jobs) {
      setTotalJobs(jobsData.jobs.total || 0);
      
      // Use the backend's hasMore directly for simple pagination
      setHasMore(jobsData.jobs.hasMore || false);
      

    }
  }, [jobsData?.jobs, currentOffset]);

  // Handle job selection for bulk operations
  const handleJobSelection = useCallback((jobId) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  }, []);

  // Function to refetch all jobs
  const refetchAllJobs = useCallback(async (forceNetworkOnly = false) => {
    // Reset pagination state
    setCurrentOffset(0);
    setHasMore(true);
    
    try {
      if (forceNetworkOnly) {
        // Force network refresh for computed field updates
        const result = await refetch({ 
          fetchPolicy: 'network-only',
          input: {
            ...queryVariables.input,
            offset: 0,
            limit: queryLimit
          }
        });
        return result;
      } else {
        // Use default cache-first for speed, mutations auto-update cache
        const result = await refetch({
          input: {
            ...queryVariables.input,
            offset: 0,
            limit: queryLimit
          }
        });
        return result;
      }
    } catch (error) {
      throw error;
    }
  }, [refetch, queryVariables, queryLimit]);



  // Proper infinite scroll using fetchMore to append jobs
  const handleLoadMore = useCallback((limit = MAX_JOBS_PER_PAGE) => {
    // If already loading, don't try to load more
    if (jobsLoading || isLoadingMore) {
      return;
    }
    
    // If no more results, stop here
    if (!hasMore) {
      return;
    }
    
    // Calculate new offset
    const newOffset = currentOffset + limit;
    setIsLoadingMore(true);
    
    // Use fetchMore to append new jobs instead of replacing them
    fetchMore({
      variables: {
        input: {
          ...queryVariables.input,
          offset: newOffset,
          limit: limit
        }
      },
      // Ensure Apollo doesn't overwrite our updateQuery result
      notifyOnNetworkStatusChange: false,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.jobs.items.length) {
          setIsLoadingMore(false);
          return prev;
        }
        
        // Update offset for next load
        setCurrentOffset(newOffset);
        
        // Combine existing and new jobs - ensure we preserve the exact structure
        const combinedResult = {
          ...prev,
          jobs: {
            ...prev.jobs,
            items: [
              ...(prev.jobs.items || []),
              ...fetchMoreResult.jobs.items
            ],
            hasMore: fetchMoreResult.jobs.hasMore,
            total: fetchMoreResult.jobs.total,
            // Preserve dateTotals from the original query or use new ones if provided
            dateTotals: fetchMoreResult.jobs.dateTotals || prev.jobs.dateTotals
          }
        };
        
        setIsLoadingMore(false);
        
        return combinedResult;
      }
    }).catch((error) => {
      setIsLoadingMore(false);
    });
  }, [hasMore, jobsLoading, isLoadingMore, currentOffset, totalJobs, fetchMore, queryVariables]);

  // Clean up effect: no scroll event listeners needed with button-based pagination
  useEffect(() => {
    return () => {
      // No cleanup needed for button-based approach
    };
  }, []);

  // Assign jobs function
  const assignJobs = useCallback((userId) => {
    if (selectedJobs.size === 0) return;
    
    assignJobsMutation({
      variables: {
        jobIds: Array.from(selectedJobs),
        userId
      },
      onCompleted: () => {
        setSelectedJobs(new Set());
        refetchAllJobs();
      }
    });
  }, [selectedJobs, assignJobsMutation, refetchAllJobs]);

  // Calculate current date range description for UI display based on actual query range
  const currentDateRangeDescription = useMemo(() => {
    try {
      // If user has selected a specific date range via date picker, show that
      if (endDate && endDate !== selectedDate && !isSameDay(selectedDate, endDate)) {
        const startStr = selectedDate ? selectedDate.toLocaleDateString() : 'Unknown';
        const endStr = endDate.toLocaleDateString();
        return `${startStr} - ${endStr}`;
      }
      
      // For property/user searches without date picker, show the default search range
      if ((searchParam && propertySearchTerm && propertySearchTerm.length >= 2) || 
          selectedPropertyId || 
          (teamMemberSearchTerm && teamMemberSearchTerm.trim())) {
        return "2 weeks back to 2 years ahead";
      }
      
      // For general infinite scroll, show the range
      return "today to 1 year ahead";
    } catch (error) {
      console.error('Error formatting date range description:', error);
      return 'Date range error';
    }
  }, [selectedDate, endDate, propertySearchTerm, selectedPropertyId, teamMemberSearchTerm]);

  return {
    // Job data - now all jobs are in pendingJobs since we're showing all non-cancelled statuses
    pendingJobs,
    pendingJobsByDate,
    allJobs,
    
    // Job totals by date
    jobsByDateTotals,
    
    // Loading states
    jobsLoading,
    isLoadingMore,
    
    // Selection
    selectedJobs,
    handleJobSelection,
    
    // Actions
    assignJobs,
    refetchAllJobs,
    handleLoadMore,
    
    // Pagination
    hasMore,
    totalJobs,
    displayedJobsCount: pendingJobs.length,
    
    // Users data
    usersData: usersData?.usersMinimal || usersData?.users, // Support both query types
    usersLoading,
    
    // Date range info - true only if user explicitly selected a different end date
    userHasSelectedDateRange: endDate && endDate !== selectedDate && !isSameDay(endDate, selectedDate),
    currentDateRangeDescription
  };
} 

export function useCanceledJobs(
  selectedDate, 
  endDate, 
  propertySearchTerm, 
  selectedCategories, 
  selectedServiceTypes,
  selectedAreas,
  user,
  selectedPropertyId = null,
  teamMemberSearchTerm = '' // Add teamMemberSearchTerm parameter
) {
  // Get Apollo client to clear cache when needed
  const client = useApolloClient();
  
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // State to track total jobs by date
  const [jobsByDateTotals, setJobsByDateTotals] = useState({});
  
  // Create a consistent search parameter - prioritize property ID over text search
  const searchParam = useMemo(() => {
    // If a property ID is selected, don't use text search
    if (selectedPropertyId) {
      return undefined;
    }
    // Otherwise, use text search if it's long enough
    const search = propertySearchTerm && propertySearchTerm.length >= 2 ? propertySearchTerm : undefined;
    return search;
  }, [propertySearchTerm, selectedPropertyId]);
  
  // Use the same query limit as regular jobs
  const queryLimit = useMemo(() => {
    // Use consistent 20 jobs per page for all scenarios - let pagination handle loading more
    return 20;
  }, []);
  
  // Format dates for GraphQL queries using the same logic as regular jobs
  const formattedStartDate = useMemo(() => {
    // If user has selected a specific startDate via date picker, use it
    if (endDate && selectedDate && !isSameDay(endDate, selectedDate)) {
      return formatStartOfDay(selectedDate);
    }
    
    // For property searches, user searches, or selected property ID, use 2 weeks back to show recent jobs
    if ((searchParam && propertySearchTerm && propertySearchTerm.length >= 2) || 
        selectedPropertyId || 
        (teamMemberSearchTerm && teamMemberSearchTerm.trim())) {
      // For property/user searches, look back 2 weeks to show recent jobs
      const twoWeeksBack = new Date();
      twoWeeksBack.setDate(twoWeeksBack.getDate() - 14);
      return formatStartOfDay(twoWeeksBack);
    }
    
    // For infinite scroll, start from today to show future jobs
    return formatStartOfDay(selectedDate);
  }, [selectedDate, endDate, searchParam, propertySearchTerm, selectedPropertyId, teamMemberSearchTerm]);
  
  const formattedEndDate = useMemo(() => {
    // If user has selected a specific endDate via date picker, use it
    if (endDate && selectedDate && !isSameDay(endDate, selectedDate)) {
      return formatEndOfDay(endDate);
    }
    
    // For property/user searches, don't use 1-year lookahead - let pagination handle loading more jobs
    if ((searchParam && propertySearchTerm && propertySearchTerm.length >= 2) || 
        selectedPropertyId || 
        (teamMemberSearchTerm && teamMemberSearchTerm.trim())) {
      // Use a reasonable lookahead (3 months) and let pagination load more as needed
      const threeMonthsAhead = new Date();
      threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3);
      return formatEndOfDay(threeMonthsAhead);
    }
    
    // For general browsing, use a broad date range to show many jobs for infinite scroll
    const broadEndDate = new Date();
    broadEndDate.setFullYear(broadEndDate.getFullYear() + 1); // Keep 1 year for non-search browsing
    return formatEndOfDay(broadEndDate);
  }, [endDate, selectedDate, searchParam, propertySearchTerm, selectedPropertyId, teamMemberSearchTerm]);

  // Reset pagination when filters change so searches always start from the beginning
  useEffect(() => {
    setCurrentOffset(0);
    setHasMore(true);
  }, [formattedStartDate, formattedEndDate, selectedCategories, selectedAreas, selectedServiceTypes, searchParam, selectedPropertyId, teamMemberSearchTerm]);

  // Clean up variables to avoid sending empty arrays or null values
  const queryVariables = useMemo(() => {
    const variables = {
      input: {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        limit: queryLimit,
        offset: 0
      }
    };
    
    // Only add optional filters if they have values
    if (selectedCategories?.length > 0) {
      variables.input.categories = selectedCategories;
    }
    if (selectedAreas?.length > 0) {
      variables.input.areas = selectedAreas;
    }
    if (selectedServiceTypes?.length > 0) {
      variables.input.serviceTypeIds = selectedServiceTypes;
    }
    if (searchParam?.trim()) {
      variables.input.search = searchParam.trim();
    }
    if (teamMemberSearchTerm?.trim()) {
      variables.input.assignedToUserId = teamMemberSearchTerm.trim();
    }
    
    // Add property ID filter if a specific property is selected
    if (selectedPropertyId) {
      variables.input.propertyId = selectedPropertyId;
    }
    
    return variables;
  }, [formattedStartDate, formattedEndDate, queryLimit, selectedCategories, selectedAreas, selectedServiceTypes, searchParam, selectedPropertyId, teamMemberSearchTerm]);

  // Main canceled jobs query
  const { 
    loading: jobsLoading, 
    data: jobsData, 
    refetch,
    fetchMore,
    error: jobsError 
  } = useQuery(GET_CANCELED_JOBS, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network', // Always check network but use cache first
    errorPolicy: 'all', // Don't break the UI on errors
    notifyOnNetworkStatusChange: true, // Important for loading states
    returnPartialData: false // Don't return partial data that could cause inconsistencies
  });

  // Debug logging removed - functionality working correctly

  // Mutations
  const [assignJobsMutation] = useMutation(ASSIGN_JOBS);
  const [cancelJobMutation] = useMutation(CANCEL_JOB);

  // Process jobs data and group by date
  const canceledJobs = useMemo(() => {
    if (!jobsData?.scheduleCanceledJobsOptimized?.items) return [];
    
    // Use current jobs from the query (Apollo handles pagination)
    const currentJobs = jobsData.scheduleCanceledJobsOptimized.items || [];
    
    // For canceled jobs, we don't need to filter by status since backend already filters
    return currentJobs;
  }, [jobsData?.scheduleCanceledJobsOptimized?.items]);

  const canceledJobsByDate = useMemo(() => {
    const grouped = {};
    
    canceledJobs.forEach(job => {
      const dateKey = formatDateKey(job.scheduledDate);
      if (dateKey) {
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(job);
      }
    });
    
    // Sort dates and jobs within each date
    Object.keys(grouped).forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        // Sort by start time if available, otherwise by ID
        if (a.startTime && b.startTime) {
          return new Date(a.startTime) - new Date(b.startTime);
        }
        return a.id - b.id;
      });
    });
    
    return grouped;
  }, [canceledJobs]);

  const allJobs = useMemo(() => {
    return canceledJobs;
  }, [canceledJobs]);

  // Update jobs by date totals when data changes
  useEffect(() => {
    if (jobsData?.scheduleCanceledJobsOptimized?.dateTotals) {
      const totals = {};
      jobsData.scheduleCanceledJobsOptimized.dateTotals.forEach(({ date, count }) => {
        let formattedDate;
        
        if (typeof date === 'number') {
          // Backend sends UTC midnight timestamps - extract UTC date components
          const utcDate = new Date(date);
          const year = utcDate.getUTCFullYear();
          const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getUTCDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else {
          formattedDate = formatDateKey(date);
        }
        
        if (formattedDate) {
          totals[formattedDate] = count;
        }
      });
      setJobsByDateTotals(totals);
    }
  }, [jobsData?.scheduleCanceledJobsOptimized?.dateTotals]);

  // Update total jobs count and use backend's hasMore
  useEffect(() => {
    if (jobsData?.scheduleCanceledJobsOptimized) {
      setTotalJobs(jobsData.scheduleCanceledJobsOptimized.total || 0);
      
      // Use the backend's hasMore directly for simple pagination
      setHasMore(jobsData.scheduleCanceledJobsOptimized.hasMore || false);
    }
  }, [jobsData?.scheduleCanceledJobsOptimized, currentOffset]);

  // Handle job selection for bulk operations
  const handleJobSelection = useCallback((jobId) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  }, []);

  // Function to refetch all jobs
  const refetchAllJobs = useCallback(async (forceNetworkOnly = false) => {
    // Reset pagination state
    setCurrentOffset(0);
    setHasMore(true);
    
    try {
      if (forceNetworkOnly) {
        // Force network refresh for computed field updates
        const result = await refetch({ 
          fetchPolicy: 'network-only',
          input: {
            ...queryVariables.input,
            offset: 0,
            limit: queryLimit
          }
        });
        return result;
      } else {
        // Use default cache-first for speed, mutations auto-update cache
        const result = await refetch({
          input: {
            ...queryVariables.input,
            offset: 0,
            limit: queryLimit
          }
        });
        return result;
      }
    } catch (error) {
      throw error;
    }
  }, [refetch, queryVariables, queryLimit]);

  // Proper infinite scroll using fetchMore to append jobs
  const handleLoadMore = useCallback((limit = MAX_JOBS_PER_PAGE) => {
    if (jobsLoading || isLoadingMore || !hasMore) return;
    
    const newOffset = currentOffset + limit;
    setIsLoadingMore(true);
    
    fetchMore({
      variables: {
        input: {
          ...queryVariables.input,
          offset: newOffset,
          limit: limit
        }
      },
      notifyOnNetworkStatusChange: false,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.scheduleCanceledJobsOptimized.items.length) {
          setIsLoadingMore(false);
          setHasMore(false);
          return prev;
        }
        
        setCurrentOffset(newOffset);
        setIsLoadingMore(false);
        
        return {
          ...prev,
          scheduleCanceledJobsOptimized: {
            ...prev.scheduleCanceledJobsOptimized,
            items: [
              ...(prev.scheduleCanceledJobsOptimized.items || []),
              ...fetchMoreResult.scheduleCanceledJobsOptimized.items
            ],
            hasMore: fetchMoreResult.scheduleCanceledJobsOptimized.hasMore,
            total: fetchMoreResult.scheduleCanceledJobsOptimized.total,
            dateTotals: fetchMoreResult.scheduleCanceledJobsOptimized.dateTotals || prev.scheduleCanceledJobsOptimized.dateTotals
          }
        };
      }
    }).catch(() => {
      setIsLoadingMore(false);
    });
  }, [hasMore, jobsLoading, isLoadingMore, currentOffset, fetchMore, queryVariables]);

  // Calculate current date range description for UI display based on actual query range
  const currentDateRangeDescription = useMemo(() => {
    try {
      // If user has selected a specific date range via date picker, show that
      if (endDate && endDate !== selectedDate && !isSameDay(selectedDate, endDate)) {
        const startStr = selectedDate ? selectedDate.toLocaleDateString() : 'Unknown';
        const endStr = endDate.toLocaleDateString();
        return `${startStr} - ${endStr}`;
      }
      
      // For property/user searches without date picker, show the default search range
      if ((searchParam && propertySearchTerm && propertySearchTerm.length >= 2) || 
          selectedPropertyId || 
          (teamMemberSearchTerm && teamMemberSearchTerm.trim())) {
        return "2 weeks back to 2 years ahead";
      }
      
      // For general infinite scroll, show the range
      return "today to 1 year ahead";
    } catch (error) {
      console.error('Error formatting date range description:', error);
      return 'Date range error';
    }
  }, [selectedDate, endDate, searchParam, propertySearchTerm, selectedPropertyId, teamMemberSearchTerm]);

  return {
    // Job data - using same structure as regular jobs
    pendingJobsByDate: canceledJobsByDate,
    allJobs,
    
    // Job totals by date
    jobsByDateTotals,
    
    // Loading states
    jobsLoading,
    isLoadingMore,
    
    // Selection
    selectedJobs,
    handleJobSelection,
    
    // Actions
    refetchAllJobs,
    handleLoadMore,
    
    // Pagination
    hasMore,
    totalJobs,
    displayedJobsCount: canceledJobs.length,
    
    // Date range info - true only if user explicitly selected a different end date
    userHasSelectedDateRange: endDate && endDate !== selectedDate && !isSameDay(endDate, selectedDate),
    currentDateRangeDescription
  };
} 